import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EvidenceManager = ({ evidenceItems, onEvidenceUpdate }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const mockEvidence = [
    {
      id: 'tx-001',
      type: 'transaction',
      title: 'Initial Ransomware Payment',
      description: 'Bitcoin transaction showing initial ransom payment of 2.5 BTC',
      hash: '1a2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z',
      amount: '2.5 BTC',
      timestamp: '2025-01-10T14:30:00Z',
      category: 'transaction',
      status: 'verified',
      attachments: ['screenshot.png', 'blockchain_proof.pdf']
    },
    {
      id: 'addr-001',
      type: 'address',
      title: 'Suspect Wallet Address',
      description: 'Primary receiving address for ransom payments',
      hash: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
      riskScore: 95,
      category: 'address',
      status: 'high-risk',
      attachments: ['risk_analysis.pdf']
    },
    {
      id: 'flow-001',
      type: 'flow-map',
      title: 'Fund Flow Diagram',
      description: 'Complete fund flow from initial payment to final destinations',
      totalAmount: '2.5 BTC',
      hops: 7,
      category: 'analysis',
      status: 'completed',
      attachments: ['flow_diagram.svg', 'analysis_report.pdf']
    },
    {
      id: 'mix-001',
      type: 'mixing',
      title: 'Mixing Service Detection',
      description: 'Evidence of funds passing through cryptocurrency mixer',
      service: 'ChipMixer',
      confidence: 87,
      category: 'analysis',
      status: 'confirmed',
      attachments: ['mixer_analysis.pdf']
    },
    {
      id: 'exch-001',
      type: 'exchange',
      title: 'Exchange Deposit',
      description: 'Final destination at centralized exchange',
      exchange: 'Binance',
      amount: '1.8 BTC',
      category: 'destination',
      status: 'traced',
      attachments: ['exchange_evidence.pdf']
    }
  ];

  const categories = [
    { id: 'all', label: 'All Evidence', icon: 'Database' },
    { id: 'transaction', label: 'Transactions', icon: 'ArrowRightLeft' },
    { id: 'address', label: 'Addresses', icon: 'MapPin' },
    { id: 'analysis', label: 'Analysis', icon: 'BarChart3' },
    { id: 'destination', label: 'Destinations', icon: 'Target' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'verified': 'text-success',
      'high-risk': 'text-error',
      'completed': 'text-primary',
      'confirmed': 'text-warning',
      'traced': 'text-success'
    };
    return colors?.[status] || 'text-text-secondary';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'transaction': 'ArrowRightLeft',
      'address': 'MapPin',
      'flow-map': 'GitBranch',
      'mixing': 'Shuffle',
      'exchange': 'Building2'
    };
    return icons?.[type] || 'File';
  };

  const filteredEvidence = mockEvidence?.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item?.category === selectedCategory;
    const matchesSearch = item?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         item?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Evidence Manager</h3>
          <p className="text-sm text-text-secondary mt-1">Manage and organize investigation evidence</p>
        </div>
        <Button variant="default" size="sm">
          <Icon name="Plus" size={16} color="currentColor" className="mr-2" />
          Add Evidence
        </Button>
      </div>
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search evidence..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`
                flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-forensic
                ${selectedCategory === category?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-text-secondary hover:text-text-primary hover:bg-muted/80'
                }
              `}
            >
              <Icon name={category?.icon} size={14} color="currentColor" />
              <span>{category?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Evidence List */}
      <div className="space-y-4">
        {filteredEvidence?.map((item) => (
          <div key={item?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-forensic">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className="flex items-center justify-center w-10 h-10 bg-muted rounded-lg">
                  <Icon name={getTypeIcon(item?.type)} size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-text-primary">{item?.title}</h4>
                    <span className={`text-xs font-medium ${getStatusColor(item?.status)}`}>
                      {item?.status}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary mb-2">{item?.description}</p>
                  
                  <div className="flex flex-wrap gap-4 text-xs text-text-secondary">
                    {item?.hash && (
                      <div className="flex items-center space-x-1">
                        <Icon name="Hash" size={12} color="currentColor" />
                        <span className="font-mono">{item?.hash?.substring(0, 16)}...</span>
                      </div>
                    )}
                    {item?.amount && (
                      <div className="flex items-center space-x-1">
                        <Icon name="DollarSign" size={12} color="currentColor" />
                        <span>{item?.amount}</span>
                      </div>
                    )}
                    {item?.riskScore && (
                      <div className="flex items-center space-x-1">
                        <Icon name="Shield" size={12} color="currentColor" />
                        <span>Risk: {item?.riskScore}%</span>
                      </div>
                    )}
                    {item?.timestamp && (
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} color="currentColor" />
                        <span>{new Date(item.timestamp)?.toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {item?.attachments && item?.attachments?.length > 0 && (
                    <div className="flex items-center space-x-2 mt-3">
                      <Icon name="Paperclip" size={14} color="var(--color-text-secondary)" />
                      <div className="flex flex-wrap gap-2">
                        {item?.attachments?.map((attachment, index) => (
                          <span key={index} className="text-xs bg-muted px-2 py-1 rounded">
                            {attachment}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                <Button variant="ghost" size="sm">
                  <Icon name="Eye" size={16} color="currentColor" />
                </Button>
                <Button variant="ghost" size="sm">
                  <Icon name="Download" size={16} color="currentColor" />
                </Button>
                <Button variant="ghost" size="sm">
                  <Icon name="MoreVertical" size={16} color="currentColor" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredEvidence?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} color="var(--color-text-secondary)" className="mx-auto mb-4" />
          <h4 className="text-lg font-medium text-text-primary mb-2">No Evidence Found</h4>
          <p className="text-text-secondary">
            {searchTerm ? 'Try adjusting your search terms' : 'No evidence items match the selected category'}
          </p>
        </div>
      )}
    </div>
  );
};

export default EvidenceManager;